import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AccessLevelTypes } from '@app/enums/AccessLevelTypes';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { AuthService } from '@services/auth/auth.service';
import { LoadingViewTriggerService } from '@services/loading-module-trigger/loading-module-trigger.service';
import { distinctUntilChanged, filter, shareReplay, switchMap, tap } from 'rxjs';

@Component({
    selector: 'medpace-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MedpaceLoginComponent implements OnInit {
    private readonly userService2 = inject(UserServiceV2);
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly loadingViewTriggerService = inject(LoadingViewTriggerService);
    private readonly destroyRef = inject(DestroyRef);

    readonly isIFrame = signal(true);
    readonly iFrameId = 'loginframe';

    private readonly isAuthenticated$ = this.authService.getIsAuthenticated().pipe(
        distinctUntilChanged(),
        shareReplay(1),
        takeUntilDestroyed(),
        tap((isAuthenticated) => {
            if (isAuthenticated) {
                this.loadingViewTriggerService.openLoading();
                this.isIFrame.set(false);
            }
        })
    );

    private readonly userData$ = this.isAuthenticated$.pipe(
        filter(Boolean),
        switchMap(() => this.userService2.hasAccount.get$()),
        tap((hasAccount) => {
            if (!hasAccount) this.loadingViewTriggerService.closeLoading();
        }),
        filter(Boolean),
        switchMap(() => this.userService2.currentUserData.get$()),
        tap((user) => {
            if (user?.isActive && user.accessGroupId !== AccessLevelTypes.NoRole) {
                this.router.navigateByUrl('dashboard');
            }
            this.loadingViewTriggerService.closeLoading();
        })
    );

    ngOnInit(): void {
        this.isAuthenticated$.subscribe();
        this.userData$.subscribe();

        const storageHandler = this.onLocalStorageChange.bind(this);
        window.addEventListener('storage', storageHandler);

        this.destroyRef.onDestroy(() => {
            window.removeEventListener('storage', storageHandler);
            this.loadingViewTriggerService.closeLoading();
        });
    }

    private onLocalStorageChange(event: StorageEvent) {
        if (event.key === 'Hide Frame' && event.oldValue === 'true') {
            this.isIFrame.set(false);
        }
    }
}
